import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import {
  changeSuperAdminLocationStatus,
  deleteSuperAdminLocation,
  getSuperAdminLocations,
  PaginatedResponse,
  SuperAdminLocation,
} from "../../services/superAdminApi";

const SuperAdminLocations: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SuperAdminLocation[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<SuperAdminLocation> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [perPage, setPerPage] = useState(15);
  const [page, setPage] = useState(1);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<SuperAdminLocation | null>(null);

  const fetchRows = async (targetPage = page, searchOverride?: string) => {
    const effectiveSearch = searchOverride ?? search;
    try {
      setLoading(true);
      setError("");
      const res = await getSuperAdminLocations({
        page: targetPage,
        per_page: perPage,
        search: effectiveSearch || undefined,
      });
      setRows(res.data || []);
      setPagination(res);
      setPage(res.current_page || targetPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load locations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage]);

  const total = pagination?.total ?? 0;
  const currentPage = pagination?.current_page ?? 1;
  const lastPage = pagination?.last_page ?? 1;
  const showDeleteColumn = total > 1;

  const handleStatusChange = async (row: SuperAdminLocation, next: "active" | "inactive") => {
    try {
      setError("");
      await changeSuperAdminLocationStatus(row.id, next);
      await fetchRows(currentPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Could not change location status.");
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await deleteSuperAdminLocation(selected.id);
      setDeleteOpen(false);
      setSelected(null);
      await fetchRows(currentPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Could not delete location.");
    }
  };

  return (
    <div className="space-y-5 p-1">
      <div>
        <h1 className="text-2xl font-semibold text-[#402B69]">Locations</h1>
        <p className="text-sm text-gray-500">Manage countries, coordinates and timezones for marketplace locations.</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-end justify-between">
        <div className="flex flex-wrap gap-3 items-end flex-1">
          <div className="min-w-[220px]">
            <label className="block text-xs text-gray-500 mb-1">Search</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Country, name, timezone..."
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Rows per page</label>
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
          <button
            type="button"
            onClick={() => fetchRows(1)}
            className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => {
              setSearch("");
              fetchRows(1, "");
            }}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Reset
          </button>
        </div>
        <button
          type="button"
          onClick={() => navigate("/superadmin/locations/create")}
          className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap"
        >
          + Create New
        </button>
      </div>

      {error ? <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2">{error}</div> : null}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-gray-600">Loading locations...</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">#</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Country</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Timezone</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-gray-500">
                        No locations found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => (
                      <tr key={row.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                        <td className="px-4 py-3 text-gray-700">{(currentPage - 1) * perPage + index + 1}</td>
                        <td className="px-4 py-3 text-gray-700">
                          {row.country?.name ? (
                            <span className="capitalize">{row.country.name}</span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700 capitalize">{row.name || "-"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.timezone?.zone_name || "-"}</td>
                        <td className="px-4 py-3 text-gray-700">
                          <select
                            value={(row.status || "active").toLowerCase() === "inactive" ? "inactive" : "active"}
                            onChange={(e) =>
                              handleStatusChange(row, e.target.value as "active" | "inactive")
                            }
                            className="border rounded-lg px-2 py-1.5 text-sm min-w-[120px]"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">In-Active</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              title="Edit"
                              onClick={() => navigate(`/superadmin/locations/${row.id}/edit`)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700"
                            >
                              <Pencil size={15} />
                            </button>
                            {showDeleteColumn ? (
                              <button
                                type="button"
                                title="Delete"
                                onClick={() => {
                                  setSelected(row);
                                  setDeleteOpen(true);
                                }}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 text-red-700"
                              >
                                <Trash2 size={15} />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="border-t px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                Page {currentPage} of {lastPage} ({total} locations)
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={currentPage <= 1 || loading}
                  onClick={() => fetchRows(currentPage - 1)}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={currentPage >= lastPage || loading}
                  onClick={() => fetchRows(currentPage + 1)}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setSelected(null);
        }}
        onConfirm={handleDelete}
        title="Delete location"
        message={`Delete location "${selected?.name || ""}"? This cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

export default SuperAdminLocations;
